import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Sparkles, ArrowRight } from "lucide-react";

const COPY = {
  pt: {
    overline: "Teste de nível · 100% grátis",
    title: "Descubra seu nível em 5 minutos.",
    body: "20 questões A1 → C1 elaboradas pelo Professor Lionel. Resultado imediato com correção comentada e seus próximos passos para evoluir.",
    cta: "Fazer o teste grátis",
    meta: ["A1 · A2 · B1 · B2 · C1", "5 minutos", "Correção comentada"],
  },
  en: {
    overline: "Level test · 100% free",
    title: "Find out your level in 5 minutes.",
    body: "20 questions A1 → C1 crafted by Professor Lionel. Instant result with commented corrections and your next steps.",
    cta: "Take the free test",
    meta: ["A1 · A2 · B1 · B2 · C1", "5 minutes", "Commented corrections"],
  },
  fr: {
    overline: "Test de niveau · 100 % offert",
    title: "Découvrez votre niveau en 5 minutes.",
    body: "20 questions A1 → C1 conçues par le Professeur Lionel. Résultat immédiat, corrections commentées et prochaines étapes.",
    cta: "Faire le test gratuit",
    meta: ["A1 · A2 · B1 · B2 · C1", "5 minutes", "Corrections commentées"],
  },
};

const LevelTestCTA = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;

  return (
    <section
      id="level-test"
      data-testid="level-test-cta-section"
      className="relative overflow-hidden bg-[#f0efea] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="overline">{t.overline}</p>
          <h2
            data-testid="level-test-cta-title"
            className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1e2a5a] leading-[1.05]"
          >
            {t.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#5c5c5c] leading-relaxed max-w-xl">
            {t.body}
          </p>
          <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
            {t.meta.map((m, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-[#d9381e]" />
                {m}
              </li>
            ))}
          </ul>
          <Link
            to="/teste-de-nivel"
            data-testid="level-test-cta-button"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e]"
          >
            <Sparkles className="h-4 w-4" />
            {t.cta}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 bg-[#1e2a5a]/5 rounded-2xl rotate-1" />
            <div className="relative bg-white border border-[#e5e3db] rounded-2xl p-8 sm:p-10">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
                <span>Question 12 / 20</span>
                <span className="font-medium text-[#d9381e]">B1</span>
              </div>
              <div className="mt-3 h-1 w-full bg-[#e5e3db] overflow-hidden rounded-full">
                <div className="h-full bg-[#1e2a5a]" style={{ width: "60%" }} />
              </div>
              <p className="font-display mt-6 text-lg sm:text-xl text-[#1e2a5a] leading-snug">
                Si j'avais le temps, je ___ en Asie.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {[
                  { l: "A", v: "voyageais", active: false },
                  { l: "B", v: "voyagerais", active: true },
                  { l: "C", v: "voyage", active: false },
                  { l: "D", v: "voyagerai", active: false },
                ].map((o) => (
                  <li
                    key={o.l}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                      o.active
                        ? "border-[#1e2a5a] bg-[#f4f1ea]"
                        : "border-[#e5e3db]"
                    }`}
                  >
                    <span
                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${
                        o.active
                          ? "bg-[#1e2a5a] border-[#1e2a5a] text-[#f4f1ea]"
                          : "border-[#5c5c5c] text-[#5c5c5c]"
                      }`}
                    >
                      {o.l}
                    </span>
                    <span className="text-[#1e2a5a]">{o.v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LevelTestCTA;
